/**
 * Pure translation lookup: dot path into a `DictNode`, `{{name}}`
 * interpolation, no string concatenation of pieces (doc 80 §1.3 — languages
 * order words differently, so a sentence is one key, not several joined).
 * Kept framework-free so it can be unit tested without React.
 */
import type { DictNode, Locale } from './dict.js'
import { dictionaries } from './dict.js'

export type Vars = Record<string, string | number>

function lookup(dict: DictNode, path: string): string | undefined {
  let node: string | DictNode = dict
  for (const segment of path.split('.')) {
    if (typeof node === 'string' || !(segment in node)) return undefined
    node = node[segment] as string | DictNode
  }
  return typeof node === 'string' ? node : undefined
}

function interpolate(template: string, vars: Vars | undefined): string {
  if (!vars) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    return name in vars ? String(vars[name]) : match
  })
}

/**
 * Resolves `key` in `locale`, falling back to Italian (the reference
 * language) and finally to the key itself so a missing translation never
 * renders as a blank — it renders as something conspicuously wrong instead,
 * which is what `dict.test.ts` exists to catch before it ships.
 */
export function translate(locale: Locale, key: string, vars?: Vars): string {
  const template = lookup(dictionaries[locale], key) ?? lookup(dictionaries.it, key) ?? key
  return interpolate(template, vars)
}
